import { CommonModule } from '@angular/common';
import {
	Component,
	ElementRef,
	OnInit,
	OnDestroy,
	TemplateRef,
	ViewChild,
	inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
	NgbActiveModal,
	NgbModal,
	NgbModalRef,
	NgbProgressbarModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from '../notification-service/notification.service';
import { NotificationHandle } from '../notification/notification-handle';
import { PDFService } from '../pdf-service/pdf.service';
import {
	HttpClient,
	HttpErrorResponse,
	HttpEventType,
} from '@angular/common/http';
import {
	catchError,
	concatMap,
	finalize,
	map,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';
import {
	Article,
	AudioArticle,
	DocArticle,
	ErrorResponse,
	ExternalItem,
	ImageArticle,
	LoginInfo,
	PDFArticle,
	TranscriptionItem,
} from '@meta-press/shared-plugins';
import {
	Observable,
	from,
	of,
	firstValueFrom,
	forkJoin,
	throwError,
} from 'rxjs';

import { LoginService } from '../login-service/login.service';
import { SourceMaterialService } from '../source-material-service/source-material.service';
import { environment } from '../../../environments/environment';
import { AiService } from '../services/ai/ai.service';
import { WhisperResponse } from '../model/WhisperResponse';
import { TranscriptionVerbose } from 'openai/resources/audio/transcriptions.mjs';
import { BackendService } from '../backend-service/backend.service';
import { AddTagsModalComponent } from '../add-tags-modal/add-tags-modal.component';
import { PDFPreviewModalComponent } from '../pdf-preview-modal/pdf-preview-modal.component';
import { PDFImageExtractorComponent } from '../pdf-image-extractor/pdf-image-extractor.component';
import { ConfirmModalComponent } from '../components/confirm-modal/confirm-modal.component';

// Upload item status type
type UploadStatus = 'pending' | 'uploading' | 'completed' | 'failed';

// Interface for storing original URL data for image extraction
interface StoredUrlData {
	originalHtml: string;
	sourceUrl: string;
	title?: string;
	extractedImageUrls?: string[];
}

// Interface for tracking file uploads
interface UploadItem {
	file: File;
	progress: number;
	status: UploadStatus;
	error?: string;
	id?: number; // ID from the server after successful upload
	statusMessage?: string; // Custom status message for detailed progress
	tags?: number[]; // Tag IDs to be added to the file
	article?: Article; // The uploaded article reference for tag management
	originalUrlData?: StoredUrlData; // Store URL extraction data for web content
	parentArticleId?: number; // Parent article ID for child articles (images extracted from PDFs/web)
	validationFailed?: boolean; // True if upload failed due to validation (not technical error)
}

@Component({
	selector: 'app-file-upload-modal',
	standalone: true,
	imports: [CommonModule, FormsModule, NgbProgressbarModule],
	templateUrl: './file-upload-modal.component.html',
	styleUrls: ['./file-upload-modal.component.scss'],
})
export class FileUploadModalComponent implements OnInit, OnDestroy {
	// Template references
	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
	@ViewChild('urlInputModal') urlInputModalTemplate!: TemplateRef<unknown>;
	@ViewChild('urlInputField') urlInputField!: ElementRef<HTMLInputElement>;

	// Services
	activeModal = inject(NgbActiveModal);
	modalService = inject(NgbModal);
	backendService = inject(BackendService);
	notificationService = inject(NotificationService);
	loginService = inject(LoginService);

	pdfService = inject(PDFService);
	sourceMaterialService = inject(SourceMaterialService);
	http = inject(HttpClient);
	aiService = inject(AiService);

	// UI state
	isDragActive = false;
	isProcessingClipboard = false;
	isProcessingUrl = false;
	showPasteInput = false; // Toggle state for paste input field
	pasteInputValue = ''; // Value of paste input field

	pendingContent: {
		type: 'text' | 'url';
		content: string;
		element?: HTMLElement;
		suggestedName?: string;
		title?: string;
		blob?: Blob;
	} | null = null;

	// Upload state
	uploadItems: UploadItem[] = [];

	// Tag state
	currentTagInput = '';
	selectedTagIds: number[] = []; // Tag IDs to be applied to all uploaded files

	// Track uploaded articles for tag management
	uploadedArticles: Article[] = [];

	// Notification handle for progress tracking
	private uploadProgressHandle: NotificationHandle | null = null;
	private clipboardProgressHandle: NotificationHandle | null = null;

	get isUploading(): boolean {
		return this.uploadItems.some((item) => item.status === 'uploading');
	}

	get hasCompletedUploads(): boolean {
		return this.uploadItems.some((item) => item.status === 'completed');
	}

	ngOnInit(): void {
		// Initialization logic can be added here if needed
	}

	/**
	 * Component cleanup - releases memory used by upload items and blob data
	 * This prevents memory leaks from large file objects and blob references
	 */
	ngOnDestroy(): void {
		// Close any open notifications
		if (this.uploadProgressHandle) {
			this.uploadProgressHandle.close();
			this.uploadProgressHandle = null;
		}
		if (this.clipboardProgressHandle) {
			this.clipboardProgressHandle.close();
			this.clipboardProgressHandle = null;
		}

		// Clear pending content (may contain blob data)
		if (this.pendingContent) {
			if (
				this.pendingContent.element &&
				this.pendingContent.element.parentNode
			) {
				this.pendingContent.element.parentNode.removeChild(
					this.pendingContent.element,
				);
			}
			this.pendingContent = null;
		}

		// Clear upload items to release File/Blob references
		this.uploadItems = [];

		// Clear uploaded articles references
		this.uploadedArticles = [];
	}

	// File drag and drop handlers
	onDragOver(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragActive = true;
	}

	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragActive = false;
	}

	onDrop(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.isDragActive = false;

		if (event.dataTransfer?.files) {
			this.handleFiles(event.dataTransfer.files);
		}
	}

	// File input handler
	onFileSelected(event: Event): void {
		const input = event.target as HTMLInputElement;
		if (input.files) {
			this.handleFiles(input.files);
		}
	}

	// Handle files from drag-drop or file input
	async handleFiles(fileList: FileList): Promise<void> {
		const files = Array.from(fileList);

		// Add files to the upload queue and validate PDFs
		for (const file of files) {
			const uploadItem: UploadItem = {
				file,
				progress: 0,
				status: 'pending',
				tags: [...this.selectedTagIds], // Copy selected tags to each file
			};

			// Validate PDF files before adding to queue
			const fileExtension = file.name.split('.').pop()?.toLowerCase();
			if (fileExtension === 'pdf') {
				const isValid = await this.validatePDFFile(file);
				if (!isValid) {
					// Mark as failed with validation error message
					uploadItem.status = 'failed';
					uploadItem.validationFailed = true;
					uploadItem.error =
						'Tiedosto ei ole tekstimuodossa. Sitä ei voida käyttää sisällön luonnissa.';
				}
			}

			this.uploadItems.push(uploadItem);
		}

		// Start uploading files (only valid ones will be uploaded)
		this.processUploadQueue();
	}

	/**
	 * Check if extracted text appears to be binary/nonsense data
	 * Returns detailed statistics about text readability
	 * @param text Text to analyze
	 * @returns Object with readability status and statistics
	 */
	private isTextReadable(text: string): {
		isReadable: boolean;
		stats: {
			totalChars: number;
			readableChars: number;
			wordChars: number;
			readableRatio: number;
			wordCharRatio: number;
		};
	} {
		if (!text || text.length === 0) {
			return {
				isReadable: false,
				stats: {
					totalChars: 0,
					readableChars: 0,
					wordChars: 0,
					readableRatio: 0,
					wordCharRatio: 0,
				},
			};
		}

		// Count different character types
		let readableChars = 0;
		let totalChars = 0;
		let wordChars = 0;

		for (const char of text) {
			totalChars++;

			// Check for readable characters (letters, numbers, common punctuation, spaces)
			// Include Finnish characters (å, ä, ö)
			if (/[a-zA-ZåäöÅÄÖ0-9\s.,!?;:()\-'"–—]/.test(char)) {
				readableChars++;
			}

			// Check for word-forming characters (letters)
			if (/[a-zA-ZåäöÅÄÖ]/.test(char)) {
				wordChars++;
			}
		}

		// Calculate ratios
		const readableRatio = readableChars / totalChars;
		const wordCharRatio = wordChars / totalChars;

		// Text is considered readable if:
		// - At least 70% of characters are readable
		// - At least 40% are actual letters (not just spaces/punctuation)
		// This helps detect binary data that might have spaces but no real words
		const isReadable = readableRatio >= 0.7 && wordCharRatio >= 0.4;

		return {
			isReadable,
			stats: {
				totalChars,
				readableChars,
				wordChars,
				readableRatio: Math.round(readableRatio * 100),
				wordCharRatio: Math.round(wordCharRatio * 100),
			},
		};
	}

	/**
	 * Validate PDF file by extracting text to check if it's readable
	 * @param file PDF file to validate
	 * @returns Promise<boolean> - true if PDF is valid and readable, false otherwise
	 */
	private async validatePDFFile(file: File): Promise<boolean> {
		try {
			console.log(`🔍 Validating PDF: ${file.name}`);
			const extractedText =
				await this.pdfService.extractTextFromPDF(file);

			if (extractedText && extractedText.length > 0) {
				// Analyze the full PDF text for readability
				const result = this.isTextReadable(extractedText);

				// Log detailed statistics
				console.log('📊 Full PDF Text Analysis:');
				console.log(`   Total characters: ${result.stats.totalChars}`);
				console.log(
					`   Readable characters: ${result.stats.readableChars} (${result.stats.readableRatio}%)`,
				);
				console.log(
					`   Word characters (letters): ${result.stats.wordChars} (${result.stats.wordCharRatio}%)`,
				);

				if (result.isReadable) {
					console.log(
						`✅ PDF is readable and contains human-readable text: ${file.name}`,
					);
					console.log(
						'📄 Sample text (first 500 chars):',
						extractedText.substring(0, 500),
					);
					return true;
				} else {
					console.warn(
						`⚠️ PDF contains binary/nonsense data instead of readable text: ${file.name}`,
					);
					console.log(
						'🔤 Sample (first 200 chars):',
						extractedText.substring(0, 200),
					);
					console.log(
						'💡 This might be an encrypted, corrupted, or improperly encoded PDF',
					);
					console.error(
						'❌ Upload blocked: PDF content is not in text format',
					);
					return false;
				}
			} else {
				console.warn(
					`⚠️ PDF appears to be empty or contains no readable text: ${file.name}`,
				);
				console.log('💡 This might be a scanned/image-based PDF');
				// Allow empty PDFs (might be scanned images) - they can still be processed
				return true;
			}
		} catch (error) {
			console.error(`❌ Error validating PDF ${file.name}:`, error);
			// Allow upload on validation errors (don't block due to technical issues)
			return true;
		}
	}

	/**
	 * Add image blobs from PDF extraction to upload queue
	 * @param imageBlobs Array of image blobs from PDF extraction
	 * @param sourceFileName Original PDF filename for naming the images
	 * @param parentArticleId Optional parent article ID for child articles
	 */
	addImageBlobsToQueue(
		imageBlobs: Blob[],
		sourceFileName: string,
		parentArticleId?: number,
	): void {
		// Generate a unique session identifier for this extraction batch
		const sessionId = Math.random().toString(36).substr(2, 8);

		// Convert blobs to File objects with appropriate names
		imageBlobs.forEach((blob, index) => {
			const baseName = sourceFileName.replace(/\.pdf$/i, '');
			const fileName = `${baseName}_image_${index + 1}_${sessionId}.png`;
			const file = new File([blob], fileName, {
				type: blob.type || 'image/png',
			});

			this.uploadItems.push({
				file,
				progress: 0,
				status: 'pending',
				tags: [...this.selectedTagIds], // Copy selected tags to each file
				parentArticleId: parentArticleId, // Set parent article ID if provided
			});
		});

		// Start uploading the new files
		this.processUploadQueue();

		// Show notification
		this.notificationService.ShowToast(
			`${imageBlobs.length} kuvaa lisätty lähetysjonoon PDF-tiedostosta`,
			'success',
		);
	}

	// Process the upload queue
	processUploadQueue(): void {
		const pendingItems = this.uploadItems.filter(
			(item) => item.status === 'pending',
		);

		if (pendingItems.length === 0) {
			// All uploads completed - add successfully uploaded files to source material service
			const completedItems = this.uploadItems.filter(
				(item) => item.status === 'completed' && item.id,
			);

			if (completedItems.length > 0) {
				this.notificationService.ShowToast(
					'Tiedostojen lataus valmis.',
					'success',
				);
				console.log(
					'Files uploaded successfully, article library updated incrementally.',
				);
			}
			return;
		}

		// Process each pending item
		const uploadObservables: Observable<ExternalItem | undefined>[] =
			pendingItems.map((item) => this.StartFileUpload(item));

		// Use concat to process uploads sequentially
		forkJoin(
			uploadObservables.map((observable) =>
				observable.pipe(
					// Handle each upload individually
					tap((response) => {
						console.log('Upload response:', response);
					}),
					// Return the original response
					map((response) => response),
				),
			),
		).subscribe({
			error: (response: HttpErrorResponse) => {
				this.notificationService.ShowToast(
					'Tiedostojen lataus epäonnistui: ' + response.error.title,
					'danger',
				);
				// Close progress notification on error
				if (this.uploadProgressHandle) {
					this.uploadProgressHandle.close();
					this.uploadProgressHandle = null;
				}
			},
			complete: () => {
				// Close progress notification when batch is complete
				if (this.uploadProgressHandle) {
					this.uploadProgressHandle.close();
					this.uploadProgressHandle = null;
				}
				this.processUploadQueue();
			},
		});
	}

	// Upload a file to the server
	StartFileUpload(item: UploadItem): Observable<ExternalItem | undefined> {
		item.status = 'uploading';

		// Create or update progress notification
		if (!this.uploadProgressHandle) {
			this.uploadProgressHandle =
				this.notificationService.createNotification(
					`Ladataan tiedostoa ${item.file.name}...`,
					{
						style: 'warning',
						timeout: -1, // No timeout for progress
						showCloseButton: true,
						progress: 0,
					},
				);
		} else {
			this.uploadProgressHandle.update({
				message: `Ladataan tiedostoa ${item.file.name}...`,
				progress: 0,
			});
		}

		// Create an Article object based on file type
		// Deduce file type from extension
		const fileType = item.file.type.split('/')[0];
		const fileExtension = item.file.name.split('.').pop()?.toLowerCase();

		let article: Article | undefined = undefined;
		const user: LoginInfo | undefined = this.loginService.GetCurrentUser();

		if (!user) {
			item.status = 'failed';
			item.error =
				'Käyttäjätietoja ei löytynyt. Tiedostoa ei voida ladata. Yritä kirjautua sisään uudelleen.';
			return of(undefined);
		}

		switch (fileExtension) {
			case 'pdf': {
				article = new PDFArticle(
					item.file.name,
					this.loginService.GetFullName(user),
					item.file.size.toString(),
					Date.now(),
					{
						type: 'url',
						url: item.file.name,
					},
					-1,
					[], // related articles
					[], // tags
					// no litteration data
				);
				break;
			}
			case 'mp3': {
				article = new AudioArticle(
					item.file.name,
					this.loginService.GetFullName(user),
					item.file.size.toString(),
					Date.now(),
					{ type: 'url', url: item.file.name },
					-1,
					[], // related articles
					[], // tags
				);
				break;
			}

			case 'webp':
			case 'png':
			case 'tif':
			case 'tiff':
			case 'jpeg':
			case 'jpg': {
				article = new ImageArticle(
					item.file.name,
					this.loginService.GetFullName(user),
					item.file.size.toString(),

					Date.now(),
					{ type: 'url', url: item.file.name },
					-1,
					[], // relatedArticleIds
					[], // tags
					undefined, // metadata
					item.parentArticleId || -1, // parentArticleId
				);

				break;
			}

			case 'doc':
			case 'docx': {
				article = new DocArticle(
					item.file.name,
					this.loginService.GetFullName(user),
					item.file.size.toString(),
					Date.now(),
					{ type: 'url', url: item.file.name },
					-1,
					fileExtension as 'doc' | 'docx' | 'txt',
					[], // related articles
					[], // tags
					// content will be extracted on backend
				);
				break;
			}

			case 'txt': {
				// For TXT files, handle like DOC - let backend extract content
				article = new DocArticle(
					item.file.name,
					this.loginService.GetFullName(user),
					item.file.size.toString(),
					Date.now(),
					{ type: 'url', url: item.file.name },
					-1,
					'txt', // fileExtension
					[], // related articles
					[], // tags
					// content will be extracted on backend
				);
				break;
			}

			case 'video': {
				item.status = 'failed';
				item.error = 'Videotiedostoja ei vielä tueta.';
				return of(undefined);
			}
		}

		if (!article) {
			item.status = 'failed';
			item.error =
				'Tiedoston käsittely epäonnistui: Tuntematon tiedostotyyppi';
			return of(undefined);
		}

		// Store the article reference in the upload item
		item.article = article;

		switch (article.type) {
			case 'audio': {
				// Transcribe first
				item.statusMessage = `Tehdään transkriptio...`;
				if (this.uploadProgressHandle) {
					this.uploadProgressHandle.update({
						message: `Tehdään äänitiedostosta ${item.file.name} transkriptio...`,
						progress: -2, // Show spinner for transcription
					});
				}

				return this.uploadFileToBackend(item).pipe(
					catchError((error: HttpErrorResponse) => {
						console.error('Failed to upload file:', error);
						item.status = 'failed';

						switch (error.status) {
							case 409:
								{
									item.error = 'Tiedosto on jo palvelimella.';
								}
								break;

							default:
								{
									item.error =
										'Tiedoston lähetys epäonnistui: ' +
										error.error;
								}
								break;
						}

						return throwError(() => error);
					}),
					tap(() => {
						item.status = 'completed';
						item.progress = 100;
						item.statusMessage = undefined;

						// Reset notification service progress if all uploads are done
					}),
				);
				break;
			}

			case 'image': {
				// For image files, get image metadata first
				item.statusMessage = `Analysoidaan ja ladataan kuva...`;

				// Get metadata from AI service
				return this.uploadFileToBackend(item).pipe(
					catchError((error: any) => {
						console.error('Failed to upload image:', error);
						// Continue upload without metadata
						item.status = 'failed';
						item.error = 'Tiedoston lähetys epäonnistui: ' + error;

						switch (error.status) {
							case 409:
								{
									item.error = 'Tiedosto on jo palvelimella.';
								}
								break;

							default:
								{
									item.error =
										'Tiedoston lähetys epäonnistui: ' +
										error.error;
								}
								break;
						}

						return throwError(() => error);
					}),
					tap(() => {
						item.status = 'completed';
						item.progress = 100;
						item.statusMessage = undefined;
					}),
				);
			}

			case 'doc': {
				// For DOC/DOCX files, content will be extracted on backend
				item.statusMessage = `Lähetetään tiedostoa...`;

				return this.uploadFileToBackend(item).pipe(
					tap(() => {
						item.status = 'completed';
						item.progress = 100;
						item.statusMessage = undefined;
					}),
					catchError((error: HttpErrorResponse) => {
						console.error('Failed to upload DOC file:', error);
						item.status = 'failed';

						switch (error.status) {
							case 409:
								{
									item.error = 'Tiedosto on jo palvelimella.';
								}
								break;

							default:
								{
									item.error =
										'Tiedoston lähetys epäonnistui: ' +
										error.error;
								}
								break;
						}

						return throwError(() => error);
					}),
				);
			}

			default: {
				return this.uploadFileToBackend(item).pipe(
					tap(() => {
						item.status = 'completed';
						item.progress = 100;
						item.statusMessage = undefined;
					}),
					catchError((error: HttpErrorResponse) => {
						console.error('Failed to upload file:', error);
						item.status = 'failed';

						switch (error.status) {
							case 409:
								{
									item.error = 'Tiedosto on jo palvelimella.';
								}
								break;

							default:
								{
									item.error =
										'Tiedoston lähetys epäonnistui: ' +
										error.error;
								}
								break;
						}

						return throwError(() => error);
					}),
				);
			}
		}
	}

	// Helper method to upload file to backend with metadata
	private uploadFileToBackend(item: UploadItem): Observable<ExternalItem> {
		const article = JSON.stringify(item.article);

		return this.backendService
			.SendFileToBackendService(item.file, article)
			.pipe(
				// Handle upload events if possible (for progress)
				tap((responseData: ExternalItem) => {
					// Update progress - Since we don't have progress events from the backend service directly,
					// we'll simulate progress between 10% and 90% until we get the final response
					const progress = 100;
					item.progress = progress;

					if (responseData && responseData.id) {
						item.id = responseData.id;
						// Update the article ID as well
						if (item.article) {
							item.article.id = responseData.id;
						}

						// Add the new article to the source material service incrementally
						const addedArticle =
							this.sourceMaterialService.addArticleFromExternalItem(
								responseData,
							);
						if (addedArticle) {
							console.log(
								'Article added to source material service:',
								addedArticle,
							);
							// Update the item's article reference with the fully processed article
							item.article = addedArticle;
						} else {
							console.error(
								'Failed to add article from ExternalItem:',
								responseData,
							);
						}
					}
				}),
				// Add tags after successful upload
				switchMap((responseData: ExternalItem) => {
					if (item.tags && item.tags.length > 0 && responseData.id) {
						// Create or get tags and then add them to the article
						return this.addTagsToArticle(
							responseData.id,
							item.tags,
						).pipe(
							map(() => responseData), // Return the original response
						);
					}
					return of(responseData);
				}),
				finalize(() => {
					// Reset notification service progress
				}),
				catchError((error: HttpErrorResponse) => {
					return throwError(() => error);
				}),
			);
	}

	// Retry a failed upload
	retryUpload(item: UploadItem): void {
		item.status = 'pending';
		item.progress = 0;
		item.error = undefined;
		item.statusMessage = undefined;
		item.validationFailed = false;
		this.StartFileUpload(item).subscribe();
	}

	// Override validation and allow upload anyway
	overrideValidationAndUpload(item: UploadItem): void {
		item.validationFailed = false;
		item.status = 'pending';
		item.error = undefined;
		item.statusMessage = undefined;
		this.processUploadQueue();
	}

	// Remove an item from the upload queue
	removeItem(item: UploadItem): void {
		const index = this.uploadItems.indexOf(item);
		if (index !== -1) {
			// Clean up references to help garbage collection
			// Nullify file reference to release blob memory
			(item as any).file = null;
			(item as any).article = null;
			(item as any).originalUrlData = null;

			this.uploadItems.splice(index, 1);

			// We need to clear file selection html element as well
			if (this.fileInput && this.fileInput.nativeElement) {
				this.fileInput.nativeElement.value = '';
			}
		}
	}

	// Cancel an upload (either in progress or failed)
	cancelUpload(item: UploadItem): void {
		const isUploading = item.status === 'uploading';

		// Set status to failed if it's currently uploading
		if (isUploading) {
			item.status = 'failed';
			item.error = 'Käyttäjä peruutti latauksen';
		} else {
			// Just remove the item if it's already failed
			this.removeItem(item);
		}
	}

	// Format file size for display
	formatFileSize(bytes: number): string {
		if (bytes === 0) {
			return '0 B';
		}

		const units = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(1024));
		return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`;
	}

	// Handle clipboard paste - can handle both text and URLs
	pasteFromClipboard(): void {
		this.isProcessingClipboard = true;

		navigator.clipboard
			.read()
			.then((clipboardItems) => {
				// Process each clipboard item
				return Promise.all(
					clipboardItems.map(async (clipboardItem) => {
						// Check for text content
						if (clipboardItem.types.includes('text/plain')) {
							const textBlob =
								await clipboardItem.getType('text/plain');
							const text = await textBlob.text();

							// Check if content is URL
							if (this.isValidUrl(text)) {
								return this.processUrlContent(text);
							} else {
								return this.processTextContent(text);
							}
						}
						// Check for image content
						else if (
							clipboardItem.types.some((type) =>
								type.startsWith('image/'),
							)
						) {
							const imageType = clipboardItem.types.find((type) =>
								type.startsWith('image/'),
							);
							if (imageType) {
								const imageBlob =
									await clipboardItem.getType(imageType);
								return this.processImageContent(imageBlob);
							}
						}

						throw new Error(
							'Leikepöydässä ei ole tuettua sisältöä (kuva, teksti tai URL)',
						);
					}),
				);
			})
			.catch((error) => {
				this.notificationService.ShowToast(
					'Leikepöydän käsittely epäonnistui: ' + error.message,
					'danger',
				);
			})
			.finally(() => {
				this.isProcessingClipboard = false;
			});
	}

	/**
	 * Toggle the visibility of the paste input field
	 * Clears the input value when hiding
	 */
	togglePasteInput(): void {
		this.showPasteInput = !this.showPasteInput;
		if (!this.showPasteInput) {
			this.pasteInputValue = ''; // Clear input when hiding
		}
	}

	/**
	 * Open the URL input modal
	 */
	openUrlModal(): void {
		// Clear previous input
		this.pasteInputValue = '';

		// Open the modal
		const modalRef = this.modalService.open(this.urlInputModalTemplate, {
			centered: true,
			size: 'md',
		});

		// Focus the input field after modal opens
		modalRef.shown.subscribe(() => {
			setTimeout(() => {
				if (this.urlInputField?.nativeElement) {
					this.urlInputField.nativeElement.focus();
				}
			}, 100);
		});
	}

	/**
	 * Process URL from the modal and close it
	 */
	processUrlFromModal(modal: NgbModalRef): void {
		const text = this.pasteInputValue.trim();

		// Validate that user entered something
		if (!text) {
			this.notificationService.ShowToast(
				'Syötä teksti tai URL-osoite',
				'warning',
			);
			return;
		}

		// Close the modal
		modal.close();

		// Clear the input value
		this.pasteInputValue = '';

		// Check if content is URL or text and process accordingly
		if (this.isValidUrl(text)) {
			this.processUrlContent(text);
		} else {
			this.processTextContent(text);
		}
	}

	/**
	 * Process the content from the paste input field
	 * Handles both URLs and plain text
	 */
	processPasteInput(): void {
		const text = this.pasteInputValue.trim();

		// Validate that user entered something
		if (!text) {
			this.notificationService.ShowToast(
				'Syötä teksti tai URL-osoite',
				'warning',
			);
			return;
		}

		// Hide the input field and clear value
		this.showPasteInput = false;
		this.pasteInputValue = '';

		// Check if content is URL or text and process accordingly
		if (this.isValidUrl(text)) {
			this.processUrlContent(text);
		} else {
			this.processTextContent(text);
		}
	}

	// Process text content
	async processTextContent(text: string): Promise<void> {
		try {
			// Create progress notification for clipboard processing
			// this.clipboardProgressHandle =
			// 	this.notificationService.createNotification(
			// 		'Valmistellaan tekstisisältöä...',
			// 		{
			// 			style: 'primary',

			// 			showCloseButton: true,
			// 			progress: -1, // Spinner
			// 		},
			// 	);

			// Get the first sentence for suggested filename
			let suggestedName = '';
			const firstSentence = text.split(/[.!?][\s\n]/)[0].trim();
			if (firstSentence) {
				// Convert first sentence to filename (max 50 chars)
				suggestedName = firstSentence
					.substring(0, 50)
					.replace(/[<>:"\\|?*/]/g, '_') // Replace invalid chars
					.trim();
			}

			// Store the content for processing after filename is provided
			this.pendingContent = {
				type: 'text',
				content: text,
				suggestedName: suggestedName || 'clipboard-content',
			};

			// Go directly to PDF preview with suggested filename
			await this.processPendingContent(
				suggestedName || 'clipboard-content',
			);
		} catch (error) {
			this.notificationService.ShowToast(
				'Tekstin käsittely epäonnistui: ' + (error as Error).message,
				'danger',
			);
			this.pendingContent = null;
		} finally {
			// Close clipboard progress notification
			if (this.clipboardProgressHandle) {
				this.clipboardProgressHandle.close();
				this.clipboardProgressHandle = null;
			}
		}
	}

	// Process image content
	async processImageContent(imageBlob: Blob): Promise<void> {
		try {
			// Create progress notification for image processing
			this.clipboardProgressHandle =
				this.notificationService.createNotification(
					'Käsitellään kuvaa...',
					{
						style: 'primary',
						timeout: -1,
						showCloseButton: true,
						progress: -2, // Spinner
					},
				);

			// Create file from the image blob
			const file = new File(
				[imageBlob],
				'clipboard-image.' + imageBlob.type.split('/')[1],
				{
					type: imageBlob.type,
				},
			);

			// Add file to upload queue
			this.uploadItems.push({
				file,
				progress: 0,
				status: 'pending',
				tags: [...this.selectedTagIds], // Copy selected tags to the file
			});

			// Start upload
			this.processUploadQueue();
		} catch (error) {
			this.notificationService.ShowToast(
				'Kuvan käsittely epäonnistui: ' + (error as Error).message,
				'danger',
			);
		} finally {
			// Close clipboard progress notification
			if (this.clipboardProgressHandle) {
				this.clipboardProgressHandle.close();
				this.clipboardProgressHandle = null;
			}
		}
	}

	// Process URL content
	async processUrlContent(url: string): Promise<void> {
		this.isProcessingUrl = true;

		// Create progress notification for URL processing
		this.clipboardProgressHandle =
			this.notificationService.createNotification(
				`Haetaan sisältöä osoitteesta ${url}...`,
				{
					style: 'primary',

					showCloseButton: true,
					progress: -1, // Spinner
				},
			);

		try {
			// Fetch the URL content using backend service
			const response = await firstValueFrom(
				this.backendService.FetchURLContent(url),
			);

			// Check if response is an error
			if (typeof response !== 'string') {
				throw new Error(
					'Failed to fetch URL content: ' + JSON.stringify(response),
				);
			}

			const html = response;

			// Update progress notification
			// if (this.clipboardProgressHandle) {
			// 	this.clipboardProgressHandle.update({
			// 		message: 'Muunnetaan sisältöä PDF-muotoon...'
			// 	});
			// }

			// Create a temporary element to render the content
			const tempElement = document.createElement('div');
			tempElement.style.width = '210mm'; // A4 width
			tempElement.style.padding = '20mm';
			tempElement.style.boxSizing = 'border-box';
			tempElement.style.position = 'absolute';
			tempElement.style.left = '-9999px';

			// Extract the main content from the HTML
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');

			// Try to get the main content by common selectors
			const mainContent =
				doc.querySelector('main') ||
				doc.querySelector('article') ||
				doc.querySelector('#content') ||
				doc.querySelector('.content') ||
				doc.body;

			if (mainContent) {
				// Clean up the content - remove scripts, styles, etc.
				const scripts = mainContent.querySelectorAll(
					'script, style, iframe, nav, footer, header',
				);
				scripts.forEach((script) => script.remove());

				// Set the content
				tempElement.innerHTML = `<h1>${doc.title}</h1><div>${mainContent.innerHTML}</div>`;
			} else {
				tempElement.innerHTML = `<h1>${doc.title}</h1><p>URL: ${url}</p>`;
			}

			document.body.appendChild(tempElement);

			try {
				// Store the content for processing after filename is provided
				const suggestedName = doc.title
					? doc.title.replace(/[<>:"\\|?*/]/g, '_').substring(0, 50)
					: url.split('/').pop() || 'web-content';

				this.pendingContent = {
					type: 'url',
					content: url,
					element: tempElement,
					suggestedName: suggestedName,
					title: doc.title,
				};

				// Go directly to PDF preview with suggested filename
				await this.processPendingContent(suggestedName);
			} finally {
				// If the modal was cancelled, clean up
				if (this.pendingContent === null && tempElement.parentNode) {
					document.body.removeChild(tempElement);
				}
			}
		} catch (error) {
			this.notificationService.ShowToast(
				'URL-sisällön hakeminen epäonnistui: ' +
					(error as Error).message,
				'danger',
			);
		} finally {
			this.isProcessingUrl = false;
			// Close clipboard progress notification
			if (this.clipboardProgressHandle) {
				this.clipboardProgressHandle.close();
				this.clipboardProgressHandle = null;
			}
		}
	}

	// Check if a string is a valid URL
	isValidUrl(str: string): boolean {
		try {
			const url = new URL(str);
			return url.protocol === 'http:' || url.protocol === 'https:';
		} catch {
			return false;
		}
	}

	// Finish the upload process and close the modal
	finishUpload(): void {
		// If there are still uploads in progress, confirm closing
		if (
			this.uploadItems.some(
				(item) =>
					item.status === 'uploading' ||
					item.status === 'pending' ||
					item.status === 'failed',
			)
		) {
			const modalRef = this.modalService.open(ConfirmModalComponent);
			modalRef.componentInstance.title = 'Sulje ikkuna';
			modalRef.componentInstance.message =
				'Tiedostojen lataus on kesken. Haluatko silti sulkea ikkunan?';
			modalRef.componentInstance.confirmButtonText = 'Sulje';
			modalRef.componentInstance.confirmButtonClass = 'btn-warning';

			modalRef.result.then((confirmed) => {
				if (!confirmed) return;

				// Return completed items and then trigger cleanup via modal close
				// Note: ngOnDestroy will handle the actual cleanup when the modal is destroyed
				const completedItems = this.uploadItems.filter(
					(item) => item.status === 'completed',
				);
				this.activeModal.close(completedItems);
			});
		} else {
			// Return completed items and then trigger cleanup via modal close
			// Note: ngOnDestroy will handle the actual cleanup when the modal is destroyed
			const completedItems = this.uploadItems.filter(
				(item) => item.status === 'completed',
			);
			this.activeModal.close(completedItems);
		}
	}

	// Helper method to trigger file input click safely
	triggerFileInput(): void {
		if (this.fileInput && this.fileInput.nativeElement) {
			this.fileInput.nativeElement.click();
		} else {
			console.error('File input element not found');
			this.notificationService.ShowToast(
				'Tiedoston valintaa ei voitu avata',
				'warning',
			);
		}
	}

	// Process the pending content with the provided filename
	async processPendingContent(filename: string): Promise<void> {
		if (!this.pendingContent) return;

		// console.error('Text to PDF not implemented.');
		// this.notificationService.ShowToast(
		// 	'Tekstin muunnos PDF-muotoon ei ole tuettu.',
		// );
		try {
			if (this.pendingContent.type === 'text') {
				await this.finalizePDFFromText(
					this.pendingContent.content,
					filename,
				);
			} else if (
				this.pendingContent.type === 'url' &&
				this.pendingContent.element
			) {
				await this.finalizePDFFromElement(
					this.pendingContent.element,
					filename,
				);
			}
		} finally {
			// Clean up
			if (
				this.pendingContent.element &&
				this.pendingContent.element.parentNode
			) {
				this.pendingContent.element.parentNode.removeChild(
					this.pendingContent.element,
				);
			}
			this.pendingContent = null;
		}
	}

	// Create PDF from text and add to upload queue
	async finalizePDFFromText(text: string, filename: string): Promise<void> {
		try {
			// Show text content preview before generating PDF (no HTML element needed)
			const modalRef = this.modalService.open(PDFPreviewModalComponent, {
				size: 'lg',
				centered: true,
				backdrop: 'static',
				keyboard: false,
			});

			// Pass data to modal for content preview
			modalRef.componentInstance.previewMode = 'content';
			modalRef.componentInstance.contentPreview = text;
			modalRef.componentInstance.filename = filename.endsWith('.pdf')
				? filename
				: `${filename}.pdf`;
			if (this.pendingContent?.title) {
				modalRef.componentInstance.title = this.pendingContent.title;
			}
			// Pass source URL if content was from a URL
			if (
				this.pendingContent?.type === 'url' &&
				this.pendingContent.content
			) {
				modalRef.componentInstance.sourceUrl =
					this.pendingContent.content;
			}

			// Wait for modal result
			const result = await modalRef.result;
			const approved = result?.approved === true;

			if (approved) {
				// Use custom filename if provided, otherwise use original filename
				const finalFilename = result?.customFilename || filename;
				const finalFilenameWithExt = finalFilename.endsWith('.pdf')
					? finalFilename
					: `${finalFilename}.pdf`;

				// Only generate PDF after user approval using text-only method
				// Create progress notification for PDF generation
				this.clipboardProgressHandle =
					this.notificationService.createNotification(
						'Tallennetaan materiaalia...',
						{
							style: 'primary',
							timeout: -1,
							showCloseButton: true,
							progress: -2, // Spinner
						},
					);

				const pdfBlob = await this.pdfService.generateTextOnlyPDF(
					text,
					this.pendingContent?.title || finalFilename,
					finalFilename.endsWith('.pdf')
						? finalFilename.slice(0, -4)
						: finalFilename,
					this.pendingContent?.type === 'url'
						? this.pendingContent.content
						: undefined,
					false, // includeImages - default to false for text-only content
				);

				// Create a file from the PDF blob using the final filename
				const file = new File([pdfBlob], finalFilenameWithExt, {
					type: 'application/pdf',
				});

				// Prepare original URL data if content came from URL
				let originalUrlData: StoredUrlData | undefined = undefined;
				if (
					this.pendingContent?.type === 'url' &&
					this.pendingContent.content
				) {
					// Parse the text to extract image URLs for later extraction
					const doc = new DOMParser().parseFromString(
						text,
						'text/html',
					);
					const imageElements = doc.querySelectorAll('img');
					const extractedImageUrls = Array.from(imageElements)
						.map((img) => img.src || img.getAttribute('src'))
						.filter(
							(src): src is string =>
								!!src && this.isValidUrl(src),
						);

					originalUrlData = {
						originalHtml: text,
						sourceUrl: this.pendingContent.content,
						title: this.pendingContent.title,
						extractedImageUrls: extractedImageUrls,
					};
				}

				// Add file to upload queue
				this.uploadItems.push({
					file,
					progress: 0,
					status: 'pending',
					tags: [...this.selectedTagIds], // Copy selected tags to the file
					originalUrlData: originalUrlData, // Store URL data for image extraction
				});

				// Start upload
				this.processUploadQueue();
			} else {
				// User cancelled, show notification
				this.notificationService.ShowToast(
					'Lataus peruutettu käyttäjän toimesta',
					'primary',
				);
			}
		} catch (error) {
			// Modal was dismissed or cancelled, or PDF generation failed
			console.log('Text PDF generation cancelled or failed:', error);
			this.notificationService.ShowToast(
				'Lataus peruutettu tai epäonnistui',
				'primary',
			);
		} finally {
			// Close clipboard progress notification
			if (this.clipboardProgressHandle) {
				this.clipboardProgressHandle.close();
				this.clipboardProgressHandle = null;
			}
		}
	}

	// Create PDF from HTML element and add to upload queue
	async finalizePDFFromElement(
		element: HTMLElement,
		filename: string,
	): Promise<void> {
		try {
			// Show content preview before generating PDF
			const previewResult = await this.showContentPreviewAndAwaitApproval(
				element,
				filename.endsWith('.pdf') ? filename : `${filename}.pdf`,
				this.pendingContent?.title,
			);

			if (previewResult.approved && previewResult.pdfBlob) {
				// Use custom filename if provided, otherwise use original filename
				const finalFilename = previewResult.customFilename || filename;
				const finalFilenameWithExt = finalFilename.endsWith('.pdf')
					? finalFilename
					: `${finalFilename}.pdf`;

				// PDF has already been generated by the preview modal
				// Create progress notification for finalizing
				this.clipboardProgressHandle =
					this.notificationService.createNotification(
						'Valmistellaan latausta...',
						{
							style: 'primary',
							timeout: -1,
							showCloseButton: true,
							progress: -2, // Spinner
						},
					);

				// Create a file from the PDF blob using the final filename
				const file = new File(
					[previewResult.pdfBlob],
					finalFilenameWithExt,
					{ type: 'application/pdf' },
				);

				// Prepare original URL data if content came from URL
				let originalUrlData: StoredUrlData | undefined = undefined;
				if (
					this.pendingContent?.type === 'url' &&
					this.pendingContent.content
				) {
					// Extract image URLs from the HTML element
					const imageElements = element.querySelectorAll('img');
					const extractedImageUrls = Array.from(imageElements)
						.map((img) => img.src || img.getAttribute('src'))
						.filter(
							(src): src is string =>
								!!src && this.isValidUrl(src),
						);

					originalUrlData = {
						originalHtml: element.innerHTML,
						sourceUrl: this.pendingContent.content,
						title: this.pendingContent.title,
						extractedImageUrls: extractedImageUrls,
					};
				}

				// Add file to upload queue
				this.uploadItems.push({
					file,
					progress: 0,
					status: 'pending',
					tags: [...this.selectedTagIds], // Copy selected tags to the file
					originalUrlData: originalUrlData, // Store URL data for image extraction
				});

				// Start upload
				this.processUploadQueue();
			} else {
				// User cancelled, show notification
				this.notificationService.ShowToast(
					'PDF-lataus peruutettu käyttäjän toimesta',
					'primary',
				);
			}
		} catch (error) {
			this.notificationService.ShowToast(
				'PDF-tiedoston luominen epäonnistui: ' +
					(error as Error).message,
				'danger',
			);
		} finally {
			// Close clipboard progress notification
			if (this.clipboardProgressHandle) {
				this.clipboardProgressHandle.close();
				this.clipboardProgressHandle = null;
			}
		}
	}

	// Add tag to the global tags list
	addTag(): void {
		const tagName = this.currentTagInput.trim();
		if (tagName) {
			const existingTag =
				this.sourceMaterialService.GetTagByName(tagName);
			if (existingTag && !this.selectedTagIds.includes(existingTag.id)) {
				this.selectedTagIds.push(existingTag.id);
				this.currentTagInput = '';
			} else if (!existingTag) {
				// Tag doesn't exist, create it first
				this.sourceMaterialService
					.AddTag({
						Name: tagName,
						Description: tagName,
					})
					.subscribe({
						next: (createdTag) => {
							this.selectedTagIds.push(createdTag.id);
							this.currentTagInput = '';
						},
						error: (error) => {
							console.error('Error creating tag:', error);
						},
					});
			}
		}
	}

	// Add existing tag from the available tags list
	addExistingTag(tagName: string): void {
		const tag = this.sourceMaterialService.GetTagByName(tagName);
		if (tag && !this.selectedTagIds.includes(tag.id)) {
			this.selectedTagIds.push(tag.id);
		}
	}

	// Remove tag from the global tags list
	removeTag(tagId: number): void {
		const index = this.selectedTagIds.indexOf(tagId);
		if (index > -1) {
			this.selectedTagIds.splice(index, 1);
		}
	}

	// Add tags to an article using SourceMaterialService
	private addTagsToArticle(
		articleId: number,
		tagIds: number[],
	): Observable<unknown[]> {
		// Use the centralized tag management from SourceMaterialService
		const tagObservables: Observable<unknown>[] = [];

		tagIds.forEach((tagId) => {
			// Tag exists by ID, just add it to the article
			tagObservables.push(
				this.sourceMaterialService.AddTagToArticle(tagId, articleId),
			);
		});

		// Execute all tag operations
		return tagObservables.length > 0 ? forkJoin(tagObservables) : of([]);
	}

	// Apply tags to all items in upload queue
	applyTagsToAllItems(): void {
		if (this.selectedTagIds.length === 0) {
			this.notificationService.ShowToast(
				'Ei tageja lisättävänä',
				'warning',
			);
			return;
		}

		// Separate items into uploaded (with ID) and not yet uploaded
		const uploadedItems = this.uploadItems.filter(
			(item) => item.id && item.status === 'completed',
		);
		const pendingItems = this.uploadItems.filter(
			(item) => !item.id || item.status !== 'completed',
		);

		// Update local state for all items immediately
		this.uploadItems.forEach((item) => {
			item.tags = [...this.selectedTagIds];
		});

		// If there are uploaded items, update them in the database
		if (uploadedItems.length > 0) {
			const tagOperations = uploadedItems
				.filter((item) => item.id !== undefined)
				.map((item) =>
					this.addTagsToArticle(
						item.id as number,
						this.selectedTagIds,
					),
				);

			// Execute database operations for uploaded files
			forkJoin(tagOperations)
				.pipe(
					catchError((error) => {
						console.error(
							'Error updating tags for uploaded files:',
							error,
						);
						this.notificationService.ShowToast(
							'Virhe tagien päivittämisessä: ' + error,
							'danger',
						);
						return of([]);
					}),
				)
				.subscribe({
					next: () => {
						const totalItems = this.uploadItems.length;
						const dbUpdatedItems = uploadedItems.length;
						const localUpdatedItems = pendingItems.length;

						let message = `Tagit päivitetty ${totalItems} tiedostoon`;
						if (dbUpdatedItems > 0 && localUpdatedItems > 0) {
							message = `Tagit päivitetty: ${dbUpdatedItems} tietokantaan, ${localUpdatedItems} latausjonoon`;
						} else if (dbUpdatedItems > 0) {
							message = `Tagit päivitetty ${dbUpdatedItems} tiedostoon tietokantaan`;
						}

						this.notificationService.ShowToast(message, 'success');
					},
				});
		} else {
			// Only pending items, just show local update message
			this.notificationService.ShowToast(
				`Tagit päivitetty ${pendingItems.length} tiedostoon latausjonossa`,
				'success',
			);
		}
	}

	// Helper methods for counting upload statuses
	getCompletedCount(): number {
		return this.uploadItems.filter((item) => item.status === 'completed')
			.length;
	}

	getUploadingCount(): number {
		return this.uploadItems.filter(
			(item) => item.status === 'uploading' || item.status === 'pending',
		).length;
	}

	getFailedCount(): number {
		return this.uploadItems.filter((item) => item.status === 'failed')
			.length;
	}

	// Open AddTagsModal for individual file or all uploaded files
	openAddTagsModal(uploadItem?: UploadItem): void {
		let articlesToTag: Article[] = [];

		if (uploadItem && uploadItem.article) {
			// Single file - use the specific article
			articlesToTag = [uploadItem.article];
		} else {
			// All uploaded files - use all completed uploads with articles
			articlesToTag = this.uploadItems
				.filter((item) => item.status === 'completed' && item.article)
				.map((item) => item.article as Article);
		}

		if (articlesToTag.length === 0) {
			this.notificationService.ShowToast(
				'Ei valittuja artikkeleita tagien lisäämistä varten',
				'warning',
			);
			return;
		}

		const modalRef = this.modalService.open(AddTagsModalComponent, {
			backdrop: 'static',
			keyboard: false,
		});

		modalRef.componentInstance.selectedArticles = articlesToTag;
	}

	// Show content preview modal and wait for user approval (previews HTML content before PDF generation)
	private async showContentPreviewAndAwaitApproval(
		element: HTMLElement,
		filename: string,
		title?: string,
	): Promise<{ approved: boolean; customFilename?: string; pdfBlob?: Blob }> {
		try {
			const modalRef = this.modalService.open(PDFPreviewModalComponent, {
				size: 'lg',
				centered: true,
				backdrop: 'static',
				keyboard: false,
			});

			// Extract text content from the HTML element for preview
			const textContent = element.innerText || element.textContent || '';

			// Pass data to modal
			modalRef.componentInstance.previewMode = 'content'; // New mode for content preview
			modalRef.componentInstance.contentPreview = textContent;
			modalRef.componentInstance.filename = filename;
			modalRef.componentInstance.htmlElement = element; // Pass HTML element for image extraction and html2canvas
			if (title) {
				modalRef.componentInstance.title = title;
			}
			// Pass source URL if content was from a URL
			if (
				this.pendingContent?.type === 'url' &&
				this.pendingContent.content
			) {
				modalRef.componentInstance.sourceUrl =
					this.pendingContent.content;
			}

			// Wait for modal result
			const result = await modalRef.result;
			return {
				approved: result?.approved === true,
				customFilename: result?.customFilename,
				pdfBlob: result?.pdfBlob,
			};
		} catch (error) {
			// Modal was dismissed or cancelled
			console.log('Content preview cancelled by user');
			return { approved: false };
		}
	}

	// Show PDF preview modal and wait for user approval (for existing PDF files)
	private async showPDFPreviewAndAwaitApproval(
		pdfBlob: Blob,
		filename: string,
		title?: string,
	): Promise<boolean> {
		try {
			const modalRef = this.modalService.open(PDFPreviewModalComponent, {
				size: 'lg',
				centered: true,
				backdrop: 'static',
				keyboard: false,
			});

			// Pass data to modal
			modalRef.componentInstance.previewMode = 'pdf'; // PDF mode
			modalRef.componentInstance.pdfBlob = pdfBlob;
			modalRef.componentInstance.filename = filename;
			if (title) {
				modalRef.componentInstance.title = title;
			}

			// Wait for modal result
			const result = await modalRef.result;
			return result?.approved === true;
		} catch (error) {
			// Modal was dismissed or cancelled
			console.log('PDF preview cancelled by user');
			return false;
		}
	}

	/**
	 * Open PDF image extractor modal and handle selected images
	 * @param uploadItem The upload item containing the PDF file
	 */
	async openPDFImageExtractor(uploadItem: UploadItem): Promise<void> {
		if (!uploadItem.file || uploadItem.file.type !== 'application/pdf') {
			this.notificationService.ShowToast(
				'Virheellinen tiedostotyyppi. Vain PDF-tiedostot tuetaan.',
				'warning',
			);
			return;
		}

		try {
			const modalRef = this.modalService.open(
				PDFImageExtractorComponent,
				{
					size: 'xl',
					centered: true,
					backdrop: 'static',
					keyboard: false,
				},
			);

			// Set the PDF file for extraction
			modalRef.componentInstance.setPdfFile(uploadItem.file);

			// Wait for modal result
			const result = await modalRef.result;

			if (result && result.success && result.imageBlobs) {
				// Generate a unique session identifier for this extraction batch
				const sessionId = Math.random().toString(36).substr(2, 8);

				// Convert image blobs to File objects and add to upload queue
				const imageFiles = result.imageBlobs.map(
					(blob: Blob, index: number) => {
						const fileName = `${uploadItem.file.name.replace('.pdf', '')}_image_${index + 1}_${sessionId}.png`;
						return new File([blob], fileName, {
							type: 'image/png',
						});
					},
				);

				// Add image files to upload queue
				imageFiles.forEach((file: File) => {
					this.uploadItems.push({
						file,
						progress: 0,
						status: 'pending',
						tags: [...this.selectedTagIds], // Copy selected tags to each file
						parentArticleId: uploadItem.id, // Set parent article ID to the PDF article
					});
				});

				// Start uploading the new image files
				this.processUploadQueue();

				// Show success notification
				this.notificationService.ShowToast(
					`${result.imageCount} kuvaa lisätty latausjonoon PDF-tiedostosta`,
					'success',
				);
			}
		} catch (error) {
			// Modal was dismissed or cancelled
			console.log('PDF image extraction cancelled by user');
		}
	}

	/**
	 * Check if an upload item is a PDF file that can have images extracted
	 * @param uploadItem Upload item to check
	 * @returns True if the item is a completed PDF upload
	 */
	canExtractImagesFromPDF(uploadItem: UploadItem): boolean {
		return (
			uploadItem.file.type === 'application/pdf' &&
			uploadItem.status === 'completed'
		);
	}

	/**
	 * Check if an upload item has URL data that can have images extracted
	 * @param uploadItem Upload item to check
	 * @returns True if the item is completed and has original URL data with image URLs
	 */
	canExtractImagesFromURL(uploadItem: UploadItem): boolean {
		return (
			uploadItem.status === 'completed' &&
			!!uploadItem.originalUrlData &&
			!!uploadItem.originalUrlData.extractedImageUrls &&
			uploadItem.originalUrlData.extractedImageUrls.length > 0
		);
	}

	/**
	 * Open URL image extractor modal and handle selected images
	 * @param uploadItem The upload item containing the URL data
	 */
	async openURLImageExtractor(uploadItem: UploadItem): Promise<void> {
		if (
			!uploadItem.originalUrlData ||
			!uploadItem.originalUrlData.extractedImageUrls
		) {
			this.notificationService.ShowToast(
				'Virheellinen URL-data. Ei kuvia saatavilla.',
				'warning',
			);
			return;
		}

		try {
			const modalRef = this.modalService.open(
				PDFImageExtractorComponent,
				{
					size: 'xl',
					centered: true,
					backdrop: 'static',
					keyboard: false,
				},
			);

			// Set the URL data for extraction
			modalRef.componentInstance.setUrlData(uploadItem.originalUrlData);

			// Wait for modal result
			const result = await modalRef.result;

			if (result && result.success && result.imageBlobs) {
				// Generate a unique session identifier for this extraction batch
				const sessionId = Math.random().toString(36).substr(2, 8);

				// Convert image blobs to File objects and add to upload queue
				const imageFiles = result.imageBlobs.map(
					(blob: Blob, index: number) => {
						const fileName = `${uploadItem.file.name.replace('.pdf', '')}_url_image_${index + 1}_${sessionId}.png`;
						return new File([blob], fileName, {
							type: 'image/png',
						});
					},
				);

				// Add image files to upload queue
				imageFiles.forEach((file: File) => {
					this.uploadItems.push({
						file,
						progress: 0,
						status: 'pending',
						tags: [...this.selectedTagIds], // Copy selected tags to each file
						parentArticleId: uploadItem.id, // Set parent article ID to the original article
					});
				});

				// Start uploading the new image files
				this.processUploadQueue();

				// Show success notification
				this.notificationService.ShowToast(
					`${result.imageCount} kuvaa lisätty latausjonoon verkkosivulta`,
					'success',
				);
			}
		} catch (error) {
			// Modal was dismissed or cancelled
			console.log('URL image extraction cancelled by user');
		}
	}
}
